import { type ComponentProps, useEffect, useState } from 'react'
import { core } from '@musinsa/pbo-core'
import { notice } from '@/components/pbo/notice'

/** 업로드 파일 항목 타입 — `core.Upload.Dragger` 의 fileList 원소에서 꺼낸다. pbo-core 루트 이름 import 는 `core` 한 통로만 허용(핸드오프 ESLint 규칙). */
type UploadFile = NonNullable<ComponentProps<typeof core.Upload.Dragger>['fileList']>[number]

interface Props {
  open: boolean
  onUpload: (file: File) => void
  onCancel: () => void
}

/**
 * 엑셀 업로드 모달 — 컨덕터 `cmp.UploadModal`(`v2Skeleton.ts buildUploadModal`) 그대로:
 * `core.Modal(엑셀 업로드·560·업로드/취소) > Flex(vertical 12) > Text(안내) + Upload dragger(문구 p 한 줄)`.
 *
 * 드래거 안은 데모 어댑터(`antdCatalog.tsx UploadRender`)와 같다 — 아이콘 없이 `<p style={{ margin: '32px 0', color: 0.65 }}>` 문구 하나.
 * 산출물은 `<p></p>`(빈 줄)로 나오는데 codegen 이 hint 기본값 '' 을 `??` 로 받아 데모 fallback 문구를 떨어뜨린 것이다(codegen gaps #18) —
 * 데모가 보여 주는 쪽을 기준으로 한다. 데모는 `showUploadList={false}`(시각 전용)지만 dev 는 고른 파일명이 보여야 하므로 파일 목록은 남긴다.
 *
 * 업로드는 서버 전송이 아니라 클라이언트 파싱이다(1.0 TOOL_IMP 파리티). 파싱 결과는 신규 행으로 표에 얹히고
 * 사용자가 검토·선택한 뒤 [저장] 이 실제 API 를 부른다. beforeUpload 에서 false 를 돌려 antd 의 자동 전송을 막는다.
 */
export function ExcelUploadModal({ open, onUpload, onCancel }: Readonly<Props>) {
  const [file, setFile] = useState<File | null>(null)
  const [fileList, setFileList] = useState<UploadFile[]>([])

  useEffect(() => {
    if (!open) return
    setFile(null)
    setFileList([])
  }, [open])

  return (
    <core.Modal
      open={open}
      title="엑셀 업로드"
      width={560}
      okText="업로드"
      cancelText="취소"
      onOk={() => {
        // 컨덕터는 확인 상시 활성 — 파일이 없으면 검증 토스트로 알린다.
        if (!file) return notice.invalid('업로드할 파일을 선택해 주세요.')
        return onUpload(file)
      }}
      onCancel={onCancel}
    >
      <core.Flex vertical gap={12} align="stretch">
        <core.Typography.Text style={{ color: 'rgba(0,0,0,0.65)' }}>양식을 내려받아 작성한 엑셀 파일을 업로드해 주세요.</core.Typography.Text>
        <core.Upload.Dragger
          accept=".xlsx"
          maxCount={1}
          fileList={fileList}
          beforeUpload={(selected) => {
            setFile(selected as File)
            return false
          }}
          onChange={({ fileList: list }) => setFileList(list.slice(-1))}
          onRemove={() => {
            setFile(null)
            setFileList([])
          }}
        >
          <p style={{ margin: '32px 0 4px', color: 'rgba(0,0,0,0.65)' }}>클릭하거나 파일을 끌어다 놓으세요</p>
          {/* 파일 제약 안내(사용자 확정 문구 2026-09-10, PR #734 리뷰 반영) — accept=".xlsx" 는 끌어다 놓기를 걸러 주지 않아 올리기 전에 알린다.
              컨덕터 그룹의 `hint` 슬롯 자리다 — 데모 어댑터는 hint 한 줄만 그리므로 데모에도 같은 문구를 넣어야 한다(컨덕터 보고 항목). */}
          <p style={{ margin: '0 0 32px', fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>엑셀 다운로드 양식과 다른 파일은 업로드 할 수 없습니다.</p>
        </core.Upload.Dragger>
      </core.Flex>
    </core.Modal>
  )
}

export default ExcelUploadModal
